import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import '../styles/CalcStatus.css';
import { HugeiconsIcon } from '@hugeicons/react';
import {
    CheckmarkCircle02Icon,
    CancelCircleIcon,
    MinusSignCircleIcon,
    CircleIcon
} from '@hugeicons/core-free-icons';
import { resolveCalcStatuses, CALC_STATES } from '../lib/underwritingStatus';
import { colour } from '../styles/GlobalStyles';
import Tooltip from './Tooltip';

const HOVER_DELAY_MS = 200;

// Single icon per calc output. Hover for the reason via styled Tooltip
// component — much nicer than the native title attribute (immediate, branded,
// readable). Tooltip only renders for non-OK states; a green check doesn't
// need an explanation.
//
// The tooltip is rendered through a portal into document.body so it can
// escape any `overflow: hidden` ancestor (notably LeadsTable cells, which
// were clipping the tooltip when this rendered inline). Positioning is
// computed from the trigger icon's bounding rect on each hover.
function CalcCell({ status }) {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const enterTimer = useRef(null);
    const exitTimer = useRef(null);

    useEffect(() => () => {
        clearTimeout(enterTimer.current);
        clearTimeout(exitTimer.current);
    }, []);

    // Compute the tooltip's screen-space position from the trigger icon.
    // Anchor sits ABOVE the icon with the beak pointing down at it. The
    // CSS pairs this `top` with `transform: translate(-50%, -100%)` so the
    // tooltip body floats above the anchor point.
    //
    // Beak math: Tooltip.css positions the beak at `bottom: -38px` inside
    // `.tooltip-inner-container`, which itself sits inside `.tooltip-outer-container`
    // with 24px vertical padding (`--corner-large`). Net result: beak tip
    // protrudes 14px below the visible body edge (38 - 24). So if we want a
    // ~6px visual gap between the beak tip and the icon top, the body's
    // bottom edge needs to be at `rect.top - 6 - 14 = rect.top - 20`.
    const measure = useCallback(() => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        setCoords({
            top: rect.top - 20,
            left: rect.left + rect.width / 2
        });
    }, []);

    const handleEnter = () => {
        clearTimeout(exitTimer.current);
        if (showTooltip) return;
        enterTimer.current = setTimeout(() => {
            measure();
            setShowTooltip(true);
            requestAnimationFrame(() => setTooltipVisible(true));
        }, HOVER_DELAY_MS);
    };

    const handleLeave = () => {
        clearTimeout(enterTimer.current);
        setTooltipVisible(false);
        exitTimer.current = setTimeout(() => setShowTooltip(false), 150);
    };

    // Recompute position on scroll/resize while open so the tooltip tracks
    // the icon if the table scrolls under it.
    useEffect(() => {
        if (!showTooltip) return undefined;
        const handler = () => measure();
        window.addEventListener('scroll', handler, true);
        window.addEventListener('resize', handler);
        return () => {
            window.removeEventListener('scroll', handler, true);
            window.removeEventListener('resize', handler);
        };
    }, [showTooltip, measure]);

    const isInformational = status.state !== CALC_STATES.OK;

    // OK state renders nothing — a confident value's own number in the cell
    // is sufficient signal; a green checkmark next to it is visual noise
    // (and on a busy table, a lot of green dots that say "everything's
    // fine" actively obscure the rows that aren't).
    if (!isInformational) return null;

    const { Icon, color } = iconFor(status.state);
    const ariaLabel = `${status.label}: ${status.reason}`;
    const isPending = status.state === CALC_STATES.PENDING;

    return (
        <span
            ref={triggerRef}
            className="calc-status-cell calc-status-cell-interactive"
            role="img"
            aria-label={ariaLabel}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
        >
            {isPending
                ? <span className="calc-status-skeleton" aria-hidden="true" />
                : <HugeiconsIcon icon={Icon} size={18} color={color} strokeWidth={2} />}
            {showTooltip
                ? createPortal(
                    <span
                        className="calc-status-tooltip-anchor"
                        style={{ top: coords.top, left: coords.left }}
                    >
                        <Tooltip
                            type="info"
                            title={status.label}
                            body={status.reason}
                            position="bottom"
                            contextTooltipBg={null}
                            exit={false}
                            exitFunc={null}
                            customClass={tooltipVisible ? 'fade-in' : 'fade-out'}
                        />
                    </span>,
                    document.body
                )
                : null}
        </span>
    );
}

function iconFor(state) {
    switch (state) {
        case CALC_STATES.OK:        return { Icon: CheckmarkCircle02Icon, color: colour.jade03 };
        case CALC_STATES.ESTIMATED: return { Icon: MinusSignCircleIcon,   color: colour.yellow02 };
        case CALC_STATES.SKIPPED:   return { Icon: CancelCircleIcon,      color: colour.red02 };
        case CALC_STATES.PENDING:
        default:                    return { Icon: CircleIcon,            color: colour.gray04 };
    }
}

export function ArvCell({ lead }) {
    const { arv } = resolveCalcStatuses(lead);
    return <CalcCell status={arv} />;
}

export function RepairsCell({ lead }) {
    const { repairs } = resolveCalcStatuses(lead);
    return <CalcCell status={repairs} />;
}

export default CalcCell;
export { CALC_STATES };
